// src/components/common/BusinessAddressForm.tsx
import { useState, useEffect, forwardRef } from 'react';
import { Search, AlertCircle, Navigation, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { addressAPI } from '../../api/addresses';
import toast from '../../utils/toast';
import type { City } from '../../types';
import CountrySelect from './CountrySelect';

interface BusinessAddressFormProps {
    value: {
        house_number: string;
        address_line_1: string;
        city_id: string;
        city_name?: string;
        country?: string;
        address_line_2?: string;
        captured_location?: {
            lat: number;
            lng: number;
        } | null;
    };
    onChange: (data: any) => void;
    errors?: {
        address_line_1?: string;
        city?: string;
        house_number?: string;
        country?: string;
    };
    onClearError?: (field: 'address_line_1' | 'city' | 'house_number' | 'country') => void;
}

const BusinessAddressForm = forwardRef<HTMLDivElement, BusinessAddressFormProps>(
    ({ value, onChange, errors = {}, onClearError }, ref) => {
        const [cities, setCities] = useState<City[]>([]);
        const [searchCity, setSearchCity] = useState(value.city_name || '');
        const [country, setCountry] = useState(value.country || 'TT');
        const [showCityDropdown, setShowCityDropdown] = useState(false);
        const [skipHouseNumber, setSkipHouseNumber] = useState(false);
        const [isCapturingLocation, setIsCapturingLocation] = useState(false);
        const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
        const [capturedLocation, setCapturedLocation] = useState<{ lat: number; lng: number } | null>(() => {
            if (value.captured_location?.lat && value.captured_location?.lng) {
                return { lat: value.captured_location?.lat, lng: value.captured_location?.lng };
            }
            return null;
        });

        useEffect(() => {
            fetchCities();
        }, []);

        const fetchCities = async () => {
            try {
                const response = await addressAPI.getCities();
                setCities(response.data || []);
            } catch (error) {
                console.error('Failed to fetch cities:', error);
                setCities([]);
            }
        };

        const filteredCities = cities.filter((city: City) =>
            city.name.toLowerCase().includes(searchCity.toLowerCase())
        );

        const handleChange = (field: string, val: any) => {
            onChange({ ...value, [field]: val });
            if (onClearError) {
                onClearError(field as 'address_line_1' | 'city' | 'house_number');
            }
        };

        const captureLocation = () => {
            if (!navigator.geolocation) {
                toast.error('Geolocation is not supported by your browser');
                return;
            }

            setIsCapturingLocation(true);
            setLocationPermissionDenied(false);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCapturedLocation({ lat: latitude, lng: longitude });

                    // Update parent with coordinates
                    onChange({
                        ...value,
                        captured_location: { lat: latitude, lng: longitude }
                    });
                    setIsCapturingLocation(false);
                    toast.success('Location captured successfully!');

                    // Optional: Reverse geocode to fill city/street
                    // You could add a reverse geocoding API call here
                },
                (error) => {
                    setIsCapturingLocation(false);
                    setLocationPermissionDenied(true);
                    console.error('Geolocation error:', error);

                    let errorMessage = 'Failed to capture location';
                    if (error.code === 1) {
                        errorMessage = 'Location permission denied. Please enable location access.';
                    } else if (error.code === 2) {
                        errorMessage = 'Location unavailable. Please try again.';
                    } else if (error.code === 3) {
                        errorMessage = 'Location request timed out. Please try again.';
                    }
                    toast.error(errorMessage);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                }
            );
        };

        const handleCityBlur = () => {
            // If user typed a city name but didn't select from dropdown
            if (searchCity && !value.city_id) {
                // Try to find a matching city
                const matchedCity = cities.find(c =>
                    c.name.toLowerCase() === searchCity.toLowerCase() ||
                    c.name.toLowerCase().includes(searchCity.toLowerCase())
                );

                if (matchedCity) {
                    // Auto-select the matching city
                    onChange({
                        ...value,
                        city_id: String(matchedCity.id),
                        city_name: matchedCity.name,
                    });
                    setSearchCity(matchedCity.name);
                    setShowCityDropdown(false);
                    if (onClearError) onClearError('city');
                    toast.info(`City set to "${matchedCity.name}"`);
                } else {
                    // Show error with guidance
                    if (onClearError) onClearError('city');
                    // The error will be shown via the parent validation
                }
            }
        };

        const clearLocation = () => {
            const newValue = { ...value, captured_location: null };
            onChange(newValue);
            setCapturedLocation(null);
            toast.info('Location cleared');
        };

        const handleCountryChange = (newCountry: string) => {
            setCountry(newCountry);
            onChange({ ...value, country: newCountry });
            if (onClearError) onClearError('country');
        };

        return (
            <div ref={ref} id="business-address-container" className="space-y-3">
                {/* House Number & Street */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            House or Lp #
                        </label>
                        <input
                            type="text"
                            name="address-house_number"
                            value={value.house_number || ''}
                            onChange={(e) => handleChange('house_number', e.target.value)}
                            className={`w-full border rounded-lg shadow-sm p-2.5 text-sm transition-colors ${errors.house_number
                                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                            placeholder="12"
                            inputMode="numeric"
                            disabled={skipHouseNumber}
                        />
                        {skipHouseNumber ? (
                            <button
                                type="button"
                                onClick={() => setSkipHouseNumber(false)}
                                className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                            >
                                Add number
                            </button>
                        ) : (
                            <p className="mt-1 text-xs text-gray-500">Just the number</p>
                        )}
                        {errors.house_number && (
                            <div className="mt-1 flex items-center text-sm text-red-600">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {errors.house_number}
                            </div>
                        )}
                    </div>

                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Street / Road / Avenue <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="address-address_line_1"
                            value={value.address_line_1 || ''}
                            onChange={(e) => handleChange('address_line_1', e.target.value)}
                            className={`w-full border rounded-lg shadow-sm p-2.5 text-sm transition-colors ${errors.address_line_1
                                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                            placeholder="e.g., Henry Road"
                        />
                        {errors.address_line_1 && (
                            <div className="mt-1 flex items-center text-sm text-red-600">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {errors.address_line_1}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Road (optional) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Main Road (optional)
                    </label>
                    <input
                        type="text"
                        value={value.address_line_2 || ''}
                        onChange={(e) => handleChange('address_line_2', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg shadow-sm p-2.5 text-sm"
                        placeholder="e.g., Eastern Main Road"
                    />
                    <p className="mt-1 text-xs text-gray-500">Helpful for finding your location</p>
                </div>

                {/* City Dropdown */}
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        City <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchCity}
                            name="address-city"
                            onChange={(e) => {
                                setSearchCity(e.target.value);
                                setShowCityDropdown(true);
                                if (value.city_id) {
                                    onChange({ ...value, city_id: '' });
                                }
                            }}
                            onFocus={() => setShowCityDropdown(true)}
                            onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                            placeholder="Search for your city..."
                            className={`w-full border rounded-lg shadow-sm pl-10 pr-10 py-2.5 text-sm transition-colors ${errors.city
                                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                        />
                        {searchCity && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchCity('');
                                    onChange({ ...value, city_id: '', city_name: '' });
                                    setShowCityDropdown(true);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {!value.city_id && searchCity && !errors.city && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="h-3 w-3" />
                            <span>Tap a city from the list to select it</span>
                        </div>
                    )}
                    {errors.city && (
                        <div className="mt-1 flex items-center text-sm text-red-600">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.city}
                        </div>
                    )}
                    {showCityDropdown && filteredCities.length > 0 && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredCities.map((city: City) => (
                                <button
                                    key={city.id}
                                    type="button"
                                    onClick={() => {
                                        onChange({
                                            ...value,
                                            city_id: String(city.id),
                                            city_name: city.name,
                                        });
                                        if (onClearError)
                                            onClearError('city');
                                        setSearchCity(city.name);
                                        setShowCityDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 ${value.city_id === String(city.id) ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                                        }`}
                                >
                                    <span className="font-medium text-gray-900">{city.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                    <CountrySelect
                        value={value.country || 'TT'}
                        onChange={handleCountryChange}
                        error={errors.country}
                        required={true}
                        className='mt-3'
                    />

                {/* Geolocation Capture */}
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-3">
                        <Navigation className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-700">Verify Business Location</p>
                                {capturedLocation ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                        <CheckCircle className="h-3 w-3" />
                                        Verified ✓
                                    </span>
                                ) : (
                                    <span className="text-xs text-gray-400">Optional</span>
                                )}
                            </div>

                            {capturedLocation ? (
                                <>
                                    <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center">
                                            <Navigation className="h-5 w-5 text-green-600 mr-2" />
                                            <div>
                                                <p className="text-sm font-medium text-green-800">Location Captured</p>
                                                <p className="text-xs text-green-600 mt-1">
                                                    Coordinates: {capturedLocation.lat.toFixed(6)}, {capturedLocation.lng.toFixed(6)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={clearLocation}
                                            className="text-xs text-red-600 hover:text-red-700 hover:underline flex items-center gap-1"
                                        >
                                            <XCircle className="h-3.5 w-3.5" />
                                            Clear Location
                                        </button>
                                        <span className="text-xs text-gray-400">|</span>
                                        <a
                                            href={`https://www.google.com/maps?q=${capturedLocation.lat},${capturedLocation.lng}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                                        >
                                            <MapPin className="h-3.5 w-3.5" />
                                            View on Map
                                        </a>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-600">
                                        Press the button below <span className="font-medium">only if you are currently at this business address</span>
                                    </p>
                                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={captureLocation}
                                            disabled={isCapturingLocation}
                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                                        >
                                            {isCapturingLocation ? (
                                                <>
                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                                    Capturing...
                                                </>
                                            ) : (
                                                <>
                                                    <Navigation className="h-4 w-4" />
                                                    I'm Here - Capture Location
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {locationPermissionDenied && !capturedLocation && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                            <span>Location access denied. You can proceed with manual address entry.</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

BusinessAddressForm.displayName = 'BusinessAddressForm';

export default BusinessAddressForm;